import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { toast } from "react-toastify";

import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";

const logisticTrackingUrl     = "/crm/logistic/trackings";
const logisticReleaseBookingUrl = "/crm/logistic/release_bookings";
const logisticForwarderUrl    = "/crm/logistic/forwarders";
const logisticDelayNoticeUrl  = "/crm/logistic/delay_notices";

export const getLogisticTrackings = createAsyncThunk(
  "Logistic/getLogisticTrackings",
  async (params) => {
    try {
      const response = await apiAxios.get(logisticTrackingUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const updateLogisticTracking = createAsyncThunk(
  "Logistic/updateLogisticTracking",
  async ({ logisticId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${logisticTrackingUrl}/${logisticId}`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data);
    }
  }
)

export const releaseBooking = createAsyncThunk(
  "Logistic/releaseBooking",
  async (formData) => {
    try {
      const response = await apiAxiosUpload.post(logisticReleaseBookingUrl, formData)
      return response.data;
    }
    catch(error) {
      throw new Error(error.response?.data?.error || error.message)
    }
  }
)

// Admin-only undo of a release booking. Unlinks this tracking row; the API
// drops the booking itself once no shipment points at it any more.
export const rollbackReleaseBooking = createAsyncThunk(
  "Logistic/rollbackReleaseBooking",
  async (logisticId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${logisticTrackingUrl}/${logisticId}/rollback_release`)
      return response.data;
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

// Admin-only: fold several rows of one job into `targetId`. The kept row keeps
// its Logistic data; the others are deleted and their plans move onto it.
export const mergeTrackings = createAsyncThunk(
  "Logistic/mergeTrackings",
  async ({ targetId, trackingIds }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${logisticTrackingUrl}/merge`, {
        target_id: targetId,
        tracking_ids: trackingIds,
      })
      return response.data;
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

// Admin-only: move `planIds` of a wrongly grouped row onto a new row.
export const splitTracking = createAsyncThunk(
  "Logistic/splitTracking",
  async ({ trackingId, planIds }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${logisticTrackingUrl}/${trackingId}/split`, { plan_ids: planIds })
      return response.data;
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

export const getLogisticMailer = createAsyncThunk(
  "Logistic/getLogisticMailer",
  async (trackingIds = []) => {
    try {
      const params = trackingIds.length > 0 ? { tracking_ids: trackingIds } : {};
      const response = await apiAxios.get(`${logisticTrackingUrl}/mailer`, { params });
      return response.data;
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

// Phát hành thông báo trễ. Backend LỌC lại các lô thật sự trễ và trả `skipped` —
// không được coi "đã gửi" là "đã gửi cho tất cả những gì tôi chọn".
export const releaseDelayNotice = createAsyncThunk(
  "Logistic/releaseDelayNotice",
  async (formData) => {
    try {
      const response = await apiAxiosUpload.post(logisticDelayNoticeUrl, formData)
      return response.data;
    }
    catch(error) {
      throw new Error(error.response?.data?.error || error.message)
    }
  }
)

// Lịch sử thông báo trễ của MỘT lô. Không nhét vào state dùng chung: mỗi lần mở là một
// lô khác nhau, giữ trong state sẽ hiện lịch sử của lô mở trước đó.
export const getDelayNotices = createAsyncThunk(
  "Logistic/getDelayNotices",
  async (trackingId) => {
    try {
      const response = await apiAxios.get(logisticDelayNoticeUrl, { params: { tracking_id: trackingId } });
      return response.data;
    }
    catch(error) {
      throw new Error(error.response?.data?.error || error.message)
    }
  }
)

export const exportData = createAsyncThunk(
  "Logistic/exportData",
  async (params) => {
    const response = await apiAxios.get(`${logisticTrackingUrl}/export`, { params, responseType: 'blob' })

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `logistic-tracking.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Without this the blob stays in memory for the life of the tab.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }
)

// Template = the rows of the current filter that this user may edit, pre-filled.
// Rows are created by CSD; Logistic only fills them in, matched by Tracking ID.
export const downloadImportTemplate = createAsyncThunk(
  "Logistic/downloadImportTemplate",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${logisticTrackingUrl}/import_template`, { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logistic-tracking-import-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      return true
    }
    catch(error) {
      return rejectWithValue({ error: 'Could not download the import template.' })
    }
  }
)

// dryRun = "Check file": the API runs every check and reports what would change,
// writing nothing. Errors are shown inside the drawer, so no global toast here.
export const importLogisticTrackings = createAsyncThunk(
  "Logistic/importLogisticTrackings",
  async ({ file, dryRun }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', dryRun ? 'true' : 'false');
      const response = await apiAxiosUpload.post(`${logisticTrackingUrl}/import`, formData, { skipErrorToast: true })
      return response.data
    }
    catch(error) {
      return rejectWithValue({ importError: error.response?.data?.error || error.message })
    }
  }
)

export const getForwarders = createAsyncThunk(
  "Logistic/getForwarders",
  async (params) => {
    try {
      const response = await apiAxios.get(logisticForwarderUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createForwarder = createAsyncThunk(
  "Logistic/createForwarder",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(logisticForwarderUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

const isRejectedAction = (action) => (action.type.startsWith("Logistic/")) && action.type.endsWith("/rejected");

// The list is always fetched when the page opens, so a stored copy of its rows
// buys nothing — it only resurfaced rows the server had already deleted when a
// load failed. Filters and paging are kept. Used by the redux-persist transform.
export const stripTrackingRows = (state) => {
  if (!state?.allData) return state;

  return {
    ...state,
    allData: { ...state.allData, data: [], totalRows: 0 },
    loading: false,
    listError: null,
    listRequestId: null,
  };
};

// Only the newest list request may write the table: an older response landing
// late (filters changed meanwhile) must not overwrite it.
const isLatestListRequest = (state, action) => action.meta?.requestId === state.listRequestId;

const logisticTrackingSlice = createSlice({
  name: "crm_logistic_tracking",
  initialState: {
    ...initialState,
    listError: null,
    listRequestId: null,
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLogisticTrackings.pending, (state, action) => {
        state.loading = true;
        state.listError = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(getLogisticTrackings.fulfilled, (state, action) => {
        if (!isLatestListRequest(state, action)) return;
        handleFulfilled(state, action, "allData");
      })
      // A failed load empties the table and says so inside it: keeping the old
      // rows next to a toast that fades made stale rows look current.
      .addCase(getLogisticTrackings.rejected, (state, action) => {
        if (!isLatestListRequest(state, action)) return;
        state.loading = false;
        state.allData.data = [];
        state.allData.totalRows = 0;
        state.listError = action.error?.message || 'Could not load the Logistic tracking list.';
      })

      .addCase(updateLogisticTracking.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(rollbackReleaseBooking.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })

      .addMatcher(
        isRejectedAction,
        (state, action) => {
          // The list reports its own failure inside the table (see above).
          if (action.type === getLogisticTrackings.rejected.type) return;
          state.loading = false;
          // The import drawer shows its own error next to the file.
          if (action.payload?.importError) return;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = logisticTrackingSlice.actions

export default logisticTrackingSlice.reducer